import {
  Ban, CalendarClock, ExternalLink, MessageSquareText, PhoneIncoming, PhoneMissed, PhoneOff, PhoneOutgoing,
  Users, Voicemail, type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Notification } from "@/components/ui/notification";
import { OutcomeTag } from "@/components/ui/outcome";
import { CallMarkBadge } from "@/features/call-mark";
import { CallTypeChip, leadCallType } from "@/features/call-type";
import { AccidentMark, AccidentOnCall } from "@/features/accident";
import { AccidentParties } from "@/features/accident-parties";
import { SamePhoneCaveat, SamePhoneRecords } from "@/features/same-phone";
import { accidentSections } from "@/lib/accident";
import { callMark } from "@/lib/call-outcome";
import { useAuth } from "@/lib/auth";
import { keptForSomebodyElse, knownZone } from "@/lib/callbacks";
import { leadName } from "@/lib/campaign";
import { attemptsText } from "@/lib/dialer";
import { outcomeGroup, recordedOutcome, type OutcomeGroup } from "@/lib/dispositions";
import { stageText } from "@/lib/contacts";
import {
  callLine, callText, cardCallbackText, contactChannelLine, languageName, listFor, localTime, type CallResult,
} from "@/lib/lead-card";
import { useCallDispositions, useLeadAccident, useLeadCard, useOutcomeHistory } from "@/lib/queries";
import { samePhoneHeadline } from "@/lib/same-phone";
import type { AgreementState, LeadCard, LeadCardCall } from "@/lib/types";
import { useNow } from "@/lib/use-now";
import { cn, formatDateTime, formatPhoneNumber, relativeTime } from "@/lib/utils";
import { useAcdCall } from "@/softphone/acd-call";
import { ScheduleActions, ScheduledOnCall } from "@/softphone/call-scheduling";

/** Records with the same phone listed on the call; the rest are a link away. */
const SIBLINGS_ON_THE_PHONE = 3;

/**
 * The lead the ACD rang this phone for (ADR-024): who they are, the time where they are, where they
 * stand on the list, and how their last call went. It is on the ringing screen, so the rep says hello
 * to a person and not to a number.
 *
 * Read from `GET /leads/{id}` once the rep's record names a lead (`useAcdCall`), and once for each
 * stay in a line. The card lists only calls that are over, so the card read for a call never has
 * that call on it; a lead who calls back a minute later is a new stay, read again, and their card
 * shows the call that just ended. Nothing personal rides the live hint that says a call is ringing
 * (ADR-018). An anonymous caller, or a lead this rep may not read, shows no card. On the call
 * screen, `full` adds the last few calls, each as a row of its own (`EarlierCalls`).
 *
 * The list's own columns (the case type, the date, the injury) are not on the card, on the phone
 * or on the Phone page: once the call is connected the rep has the contact's form beside it
 * (`softphone/call-form.tsx`), and the same answers twice is one of them to distrust. Open record
 * is the way to everything else about the person.
 *
 * The firm and the stage are the CRM's (ADR-044, -045, -046): the firm the lead belongs to, and the
 * stage in the tone the contacts list gives it. Open record goes to the contact — the timeline, the
 * notes, Set outcome — without leaving the call. A callback promised and not yet followed by an
 * outcome (ADR-080) is said too, with its backup time (ACL-559): the rep calling back knows it is the
 * call they promised. On the call the card also books an appointment, keeps a callback time picked
 * before the call ends, and lists both with the lead's upcoming appointments (`call-scheduling.tsx`,
 * ACL-559).
 *
 * `frame` false draws it without its box, for the Phone page's column, which is the card's alone
 * and already ruled off from the band above it and the form beside it: a box inside that column
 * was a frame in a frame. Among the phone's other blocks (its own panel, the ringing stage) the
 * box is what holds the card together, and it stays.
 *
 * Other records with the phone (ACL-336) are counted while it rings and listed on the call, each
 * with what happened to it, so a number that has been here before is not met as a new one. On the
 * call each also says whether somebody marked it the same accident, and anyone linked into the
 * caller's accident is named (ACL-337), read apart from the card so a fault there cannot blank it.
 * On the call, Add a person makes the record of somebody else in the caller's accident (ACL-510).
 */
export function LeadCardPanel({
  callId, full = false, frame = true, actions,
}: {
  callId: string;
  full?: boolean;
  frame?: boolean;
  actions?: CardActions;
}) {
  const acd = useAcdCall();
  const leadId = acd !== null && acd.callId === callId ? acd.leadId : null;
  const { data: card } = useLeadCard(leadId, acd?.sessionId ?? acd?.callerChannelId ?? null);
  const campaignId = acd?.line?.campaign_id ?? null;
  // The call an outcome will be asked for, once it is up: what a callback time is kept for (ACL-559).
  const liveCall = acd !== null && acd.state === "busy" ? acd.callerChannelId : null;
  if (leadId === null || !card || card.id !== leadId) return null;
  return (
    <LeadCardView card={card} campaignId={campaignId} full={full} frame={frame} actions={actions} callId={liveCall} line={acd?.line ?? null} />
  );
}

/**
 * The same card for a lead the page names by id: in the outcome window, and for the lead the Phone
 * page keeps after their call (`useWorkLead`), when the rep's record no longer names anybody. Read
 * with the same key as the call's card, so a card read during the call is not read again.
 */
export function LeadCardFor({
  leadId, stay, campaignId, full = false, frame = true, actions,
}: {
  leadId: string;
  /** The caller's stay in the line, when something names it (`useLeadCard`'s second key). */
  stay: string | null;
  campaignId: string | null;
  full?: boolean;
  frame?: boolean;
  actions?: CardActions;
}) {
  const { data: card } = useLeadCard(leadId, stay);
  if (!card || card.id !== leadId) return null;
  return <LeadCardView card={card} campaignId={campaignId} full={full} frame={frame} actions={actions} />;
}

/** What a page puts beside the card's Open record for the lead the card shows: the Phone page's Send
 *  contract (ACL-560). Given the card, so an action is always about the lead on screen, and the row it
 *  stands on: the caller's row of the accident panel (ACL-671), where it is drawn as that row's other
 *  actions are, or null at the card's top, where there is no panel. */
export type CardActions = (card: LeadCard, row: CardRow | null) => ReactNode;
/** The caller's row of the accident panel: their agreement, as the accident route says it. */
export interface CardRow {
  agreement: AgreementState | null;
}

/** The card itself, once somebody has read the lead: `LeadCardPanel` for the ACD's call, and
 *  `LeadCardFor` for a lead named by id. `campaignId` picks the list the lead's attempts are
 *  counted on and the outcomes their last calls are worded with. */
export function LeadCardView({
  card, campaignId, full = false, frame = true, actions, callId = null, line = null,
}: {
  card: LeadCard;
  campaignId: string | null;
  full?: boolean;
  /** Whether the card is drawn in a box of its own (`LeadCardPanel`). */
  frame?: boolean;
  actions?: CardActions;
  /** The ACD's call with this lead while it is up, which a callback time is picked for (ACL-559). */
  callId?: string | null;
  /** The line the call came on: its campaign is the list a party added on the call may be called
   *  from (ADR-225). */
  line?: { campaign_id: string | null; campaign_name: string | null } | null;
}) {
  const outcomes = useCallDispositions(campaignId, campaignId !== null);
  // The clock the dialer calls the lead on: their own zone, else their state's (ACL-495).
  const zone = card.clock_timezone ?? card.timezone;
  const now = useNow(zone ? 30_000 : null);
  const { user } = useAuth();
  // Somebody else's callback, and this rep has the lead now: the dialer gave them the call, their
  // rep not being there to take it (ADR-162 in `platform`). Said with what it is for and the note.
  const handed = keptForSomebodyElse(card.callback, user?.id) ? card.callback : null;

  // An old call's switched-off outcome still reads by its label (ACL-574).
  const history = useOutcomeHistory(campaignId !== null);
  const label = (slug: string) => recordedOutcome(slug, outcomes.data, history.data)?.label;
  // The colour an outcome carries on every screen (ADR-160), from the same list as its words.
  const group = (slug: string | null) => {
    const found = recordedOutcome(slug, outcomes.data, history.data);
    return found ? outcomeGroup(found) : null;
  };
  const name = leadName(card);
  const stage = card.stage ? stageText(card.stage) : null;
  const channelLine = contactChannelLine(card);
  const clock = localTime(zone, now);
  const where = [clock && `${clock} there`, card.state, languageName(card.language)].filter(Boolean);
  const list = listFor(card, campaignId);
  const calls = card.calls.slice(0, full ? 3 : 1);
  const siblings = card.siblings ?? [];
  const headline = samePhoneHeadline(siblings);
  // Read on the call only, apart from the card, so a fault there leaves the card as it is (ACL-337).
  const accident = useLeadAccident(full ? card.id : null);
  const { byPhone } = accidentSections(accident.data);
  // The accident's people, where each stands and what to do for each (ACL-671): on the call screen, once
  // the accident is read. A record in no accident, or an older core-api, keeps the blocks below instead.
  const parties = full && accident.data?.accident ? accident.data : null;

  return (
    <section
      aria-label="The lead"
      className={cn(
        "w-full text-left text-xs",
        // Without its box the card keeps a little air at its sides, so its words do not start
        // on the very line the column's own padding ends at.
        frame ? "rounded-lg border border-border px-3 py-2" : "px-1",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-text">
            {name ?? formatPhoneNumber(card.phone)}
          </p>
          {where.length > 0 && <p className="truncate text-text-muted">{where.join(" · ")}</p>}
          {card.firm && <p className="truncate text-text-muted">{card.firm.label}</p>}
        </div>
        {stage && (
          <span
            className={cn(
              "shrink-0 rounded-md px-1.5 py-0.5 text-2xs font-medium",
              stage.tone === "warning"
                ? "bg-warning-2 text-warning-11"
                : stage.tone === "danger"
                  ? "bg-danger-2 text-danger-11"
                  : stage.tone === "neutral"
                    ? "bg-gray-3 text-gray-11"
                    : "bg-accent-3 text-accent-11",
            )}
            title={stage.description}
          >
            {stage.label}
          </span>
        )}
      </div>

      {/* A page's own actions, right under who the lead is (Send contract, ACL-560; Create new contact,
          ACL-626): on screen the moment the call connects, above the history a rep would otherwise
          scroll past. Side by side, the second going under the first on a card too narrow for both. */}
      {actions && !parties && <div className="mt-2 flex flex-wrap items-center gap-1.5">{actions(card, null)}</div>}

      {card.dnc && (
        <p className="mt-1.5 flex items-center gap-1 font-medium text-danger-11">
          <Ban className="size-3.5 shrink-0" aria-hidden />
          Marked do not call
        </p>
      )}

      {channelLine && (
        <p className="mt-1.5 flex items-center gap-1 font-medium text-warning-11">
          <MessageSquareText className="size-3.5 shrink-0" aria-hidden />
          {channelLine}
        </p>
      )}

      {card.callback && (
        <p
          className={cn(
            "mt-1 flex items-start gap-1",
            card.callback.status === "due" ? "text-warning-11" : "text-accent-11",
          )}
        >
          <CalendarClock className="mt-px size-3.5 shrink-0" aria-hidden />
          <span className="min-w-0">{cardCallbackText(card.callback, knownZone(zone))}</span>
        </p>
      )}
      {card.callback?.note && !handed && (
        <p className="mt-0.5 pl-[1.125rem] text-text-muted">Note: {card.callback.note}</p>
      )}
      {handed && (
        <Notification
          className="mt-1.5 rounded-md border border-warning-4"
          compact
          unread
          tone="warning"
          title="Someone else's callback"
          message={[`Kept for ${handed.rep ?? "another rep"}`, handed.purpose].filter(Boolean).join(" · ")}
          details={[
            handed.note ? `Note: ${handed.note}` : null,
            "You have the call now: their rep wasn't there to take it.",
          ].filter((line): line is string => line !== null)}
        />
      )}
      {/* On the call: the time picked for the callback and the appointments to come (ACL-559). */}
      {full && <ScheduledOnCall card={card} callId={callId} />}

      {parties && (
        <AccidentParties
          card={card}
          accident={parties}
          campaignId={campaignId ?? card.calling_campaign?.id ?? null}
          onCall={callId !== null}
          ownActions={actions?.(card, { agreement: parties.agreement ?? null })}
        />
      )}

      <EarlierCalls
        calls={calls}
        // How often the list has called them. While the phone rings it says how the last of
        // those went too; on the call the rows under it say how each call went.
        summary={
          list === null || list.attempts === 0
            ? null
            : attemptsText(full && calls.length > 0 ? { ...list, last_disposition: null } : list, label)
        }
        full={full}
        label={label}
        group={group}
      />



      {headline && !parties &&
        (full ? (
          <section aria-label="Same phone" className="mt-2 border-t border-border pt-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-2xs font-semibold uppercase tracking-wide text-text-subtle">Same phone</p>
              <Badge tone={headline.flag ? "warning" : "neutral"} size="sm">
                {siblings.length} {siblings.length === 1 ? "record" : "records"}
                {headline.flag && ` · ${headline.flag}`}
              </Badge>
            </div>
            <SamePhoneRecords
              siblings={siblings}
              phone={card.phone}
              limit={SIBLINGS_ON_THE_PHONE}
              compact
              caveat={false}
              footer={(sibling) => <AccidentMark leadId={card.id} record={byPhone.get(sibling.id)} compact />}
            />
            <SamePhoneCaveat compact className="pb-0.5" />
          </section>
        ) : (
          <p
            className={cn(
              "mt-1.5 flex items-start gap-1.5 rounded-md border px-2 py-1",
              headline.flag
                ? "border-warning-4 bg-warning-2 text-warning-11"
                : "border-accent-4 bg-accent-2 text-accent-11",
            )}
          >
            <Users className="mt-px size-3.5 shrink-0" aria-hidden />
            <span className="min-w-0">
              <span className="block font-medium">{headline.text}</span>
              {headline.flag && <span className="block font-semibold">{headline.flag}</span>}
            </span>
          </p>
        ))}

      {full && !parties && <AccidentOnCall accident={accident.data} />}

      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <Link
          to={`/contacts/${card.id}`}
          className="inline-flex items-center gap-1 text-2xs font-medium text-accent-11 hover:underline"
        >
          <ExternalLink className="size-3" aria-hidden />
          Open record
        </Link>
        {/* Booked and picked while they are on the line, not after (ACL-559). */}
        {full && <ScheduleActions card={card} callId={callId} />}
      </div>
    </section>
  );
}

/** A call's mark on the card, by how it went: the tone of its disc, and the icon where the way
 *  the call went does not pick one (a conversation is drawn by its direction). */
const CALL_MARKS: Record<CallResult, { icon: LucideIcon; tone: string }> = {
  talked: { icon: PhoneOutgoing, tone: "bg-success-2 text-success-11" },
  voicemail: { icon: Voicemail, tone: "bg-warning-2 text-warning-11" },
  no_answer: { icon: PhoneMissed, tone: "bg-gray-3 text-gray-11" },
  busy: { icon: PhoneOff, tone: "bg-gray-3 text-gray-11" },
  failed: { icon: PhoneOff, tone: "bg-danger-2 text-danger-11" },
  // Somebody who called in and got nobody: worth a second look before saying hello.
  missed: { icon: PhoneMissed, tone: "bg-warning-2 text-warning-11" },
};

/**
 * The lead's earlier calls, newest first, under how often the list has called them.
 *
 * On the call screen (`full`) each call is a row: a mark in the tone of how it went, what happened
 * and when, then how it was placed, which call of its cadence it was (`CallMarkBadge`) and the
 * outcome recorded for it. A line joins one mark to the next, so the rows read as one history.
 * They were sentences once ("Voicemail · power dial · Voicemail"), which a rep saying hello had
 * to read in full to learn anything from. While the phone rings there is one call, on one line:
 * Answer matters most there.
 */
function EarlierCalls({
  calls, summary, full, label, group,
}: {
  calls: LeadCardCall[];
  summary: string | null;
  full: boolean;
  label: (slug: string) => string | undefined;
  group: (slug: string | null) => OutcomeGroup | null;
}) {
  if (calls.length === 0 || !full) {
    const last = calls[0];
    const mark = last ? markOf(last, callLine(last, label).result) : null;
    const text = last ? callText(last, label, { withOutcome: false }) : null;
    const outcome = last ? callLine(last, label).outcome : null;
    return (
      <>
        {summary && <p className="mt-1 text-text-muted">{summary}</p>}
        {last && mark && text && (
          <p className="mt-1 flex min-w-0 items-center gap-1.5" aria-label="Last call">
            <span
              className={cn("flex size-4 shrink-0 items-center justify-center rounded-full", mark.tone)}
            >
              <mark.icon className="size-2.5" aria-hidden />
            </span>
            <span className="min-w-0 truncate text-text" title={text}>
              {text}
            </span>
            {outcome && (
              <OutcomeTag group={group(last.disposition)} className="max-w-[50%] shrink-0" title="The outcome recorded for this call">
                {outcome}
              </OutcomeTag>
            )}
            <span className="flex-1" />
            <When at={last.at} />
          </p>
        )}
      </>
    );
  }
  return (
    <div className="mt-2 border-t border-border pt-2">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="shrink-0 text-2xs font-semibold uppercase tracking-wide text-text-subtle">
          Previous calls
        </h3>
        {summary && (
          <span className="min-w-0 truncate text-2xs text-text-muted" title={summary}>
            {summary}
          </span>
        )}
      </div>
      <ol className="mt-1.5">
        {calls.map((call, i) => {
          const line = callLine(call, label);
          const mark = markOf(call, line.result);
          const talked = line.result === "talked";
          // Who a conversation was with reads on with its length; how a dial was placed is the
          // smaller fact, under it.
          const how = talked ? null : line.detail;
          const type = leadCallType(call);
          const second = type !== null || how !== null || callMark(call) !== null || line.outcome !== null;
          return (
            <li key={`${call.via}-${call.at}`} className="relative flex gap-2 pb-2.5 last:pb-0">
              {i < calls.length - 1 && (
                <span aria-hidden className="absolute bottom-0 left-3 top-6 w-px -translate-x-1/2 bg-border" />
              )}
              <span
                className={cn("flex size-6 shrink-0 items-center justify-center rounded-full", mark.tone)}
              >
                <mark.icon className="size-3" aria-hidden />
              </span>
              <div className="min-w-0 flex-1 pt-0.5">
                <p className="flex items-baseline gap-2">
                  <span className="min-w-0 flex-1 text-text">
                    <span className="font-medium">{line.title}</span>
                    {talked && line.detail && ` ${line.detail}`}
                  </span>
                  <When at={call.at} />
                </p>
                {second && (
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-2xs text-text-muted">
                    {type && <CallTypeChip type={type} size="sm" />}
                    {how}
                    <CallMarkBadge call={call} />
                    {line.outcome && (
                      <OutcomeTag group={group(call.disposition)} title="The outcome recorded for this call">
                        {line.outcome}
                      </OutcomeTag>
                    )}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** A call's mark: its result's tone, and for a conversation the icon of the way the call went. */
function markOf(call: LeadCardCall, result: CallResult): { icon: LucideIcon; tone: string } {
  const mark = CALL_MARKS[result];
  return result === "talked" && call.direction === "inbound" ? { ...mark, icon: PhoneIncoming } : mark;
}

/** When a call was, as the card says it ("yesterday"), with the day and hour behind it. */
function When({ at }: { at: string }) {
  return (
    <time dateTime={at} title={formatDateTime(at)} className="shrink-0 text-2xs text-text-subtle">
      {relativeTime(at)}
    </time>
  );
}
